import { useState, useEffect, useCallback, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { todayLocalISO, expireRigSession, isRigExpired } from '@/lib/kiosk';
import useResilientSubscription from '@/hooks/useResilientSubscription';

const HIDDEN_BOOKING_STATUSES = ['pending', 'cancelled'];

/**
 * Loads everything the venue kiosk needs and runs the one-second floor clock
 * that drives the countdowns and the session-expiry sweep.
 */
export default function useKioskData(enabled) {
  const [bookings, setBookings] = useState([]);
  const [rigs, setRigs] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [experiences, setExperiences] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);

  const rigsRef = useRef([]);
  const sweepingRef = useRef(false);

  const load = useCallback(async () => {
    const today = todayLocalISO();
    const [bookingRows, rigRows, feedRows, experienceRows] = await Promise.all([
      base44.entities.Booking.filter({ date: today }, 'time_slot', 200),
      base44.entities.BookingRig.list('bay_number', 100),
      base44.entities.RigLiveFeed.list('-sort_order', 100),
      base44.entities.BookingExperience.list('sort_order', 100),
    ]);
    setBookings((bookingRows || []).filter((b) => !HIDDEN_BOOKING_STATUSES.includes(b.status)));
    setRigs(rigRows || []);
    setFeeds(feedRows || []);
    setExperiences(Object.fromEntries((experienceRows || []).map((e) => [e.key, e.name])));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load().finally(() => setLoading(false));
  }, [enabled, load]);

  useEffect(() => {
    rigsRef.current = rigs;
  }, [rigs]);

  // A booking taken at the desk, a dispatch from the till or a bay left running
  // all happen on another device, and the telemetry heartbeats behind the bay
  // statuses change every few seconds — so the kiosk re-reads on the event and on
  // a steady cadence, and rebuilds its connection whenever the iPad wakes.
  useResilientSubscription({
    entities: ['Booking', 'BookingRig', 'BookingExperience'],
    enabled,
    onEvent: () => { load(); },
    onResync: () => { load(); },
    pollMs: 10000,
    pollWhileActive: true,
  });

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [load]);

  // One-second floor clock: countdowns + auto-flip finished sessions to cleaning.
  // Countdowns are recomputed from each bay's own expiry timestamp, so they are
  // correct again the instant the screen wakes rather than resuming a stale timer.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(async () => {
      setTick((t) => t + 1);
      const expired = rigsRef.current.filter(isRigExpired);
      if (!expired.length || sweepingRef.current) return;
      sweepingRef.current = true;
      try {
        for (const rig of expired) await expireRigSession(rig);
        await load();
      } finally {
        sweepingRef.current = false;
      }
    }, 1000);
    return () => clearInterval(id);
  }, [enabled, load]);

  return { bookings, rigs, feeds, experiences, loading, refreshing, refresh, tick };
}